// react
import { SyntheticEvent, useContext, useState } from 'react';

// types
import {
	Document,
	Entity,
	Loan,
	MetadataField,
	Pagination,
	Payment,
	Transaction,
	User,
} from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// tables
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicNonCashBenefits } from '../../tables/basic/TableBasicNonCashBenefits/TableBasicNonCashBenefits';
import { TableBasicPayments } from '../../tables/basic/TableBasicPayments/TableBasicPayments';
import { TableBasicUsers } from '../../tables/basic/TableBasicUsers/TableBasicUsers';

// ui
import { Amount } from '../../ui/Amount/Amount';
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { ChipTraceability } from '../../ui/ChipTraceability/ChipTraceability';
import { IconCircle, IconCircleType } from '../../ui/IconCircle/IconCircle';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { Metadata } from '../../ui/Metadata/Metadata';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs } from '../../ui/Tabs/Tabs';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// dialogs
import { DialogAddNonCashBenefit } from '../../dialogs/DialogAddNonCashBenefit/DialogAddNonCashBenefit';
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogEditBeneficiary } from '../../dialogs/DialogEditBeneficiary/DialogEditBeneficiary';
import { DialogUpdateEntityStatus } from '../../dialogs/DialogUpdateEntityStatus/DialogUpdateEntityStatus';
import { DialogUploadDocsEntity } from '../../dialogs/DialogUploadDocsEntity/DialogUploadDocsEntity';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// icons
import {
	DoDisturbOutlined as IconDoDisturb,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
	PaidOutlined as IconPaid,
	PowerSettingsNewOutlined as IconPowerSettingsNew,
	VolunteerActivismOutlined as IconVolunteerActivism,
} from '@mui/icons-material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { defaultPagination } from '../../../utils/defaults';
import { translateType } from '../../../utils/translations';

// theme
import { theme } from '../../../theme';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface CardBeneficiaryProps {
	beneficiary: Entity;
	refetchBeneficiaries?: () => void;
}

export const CardBeneficiary = ({ beneficiary, refetchBeneficiaries }: CardBeneficiaryProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// params
	const [searchParams, setSearchParams] = useSearchParams();
	const paramDialog = searchParams.get('dialog');
	const paramDialogBeneficiary = searchParams.get('dialogBeneficiary');

	// hooks
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// state
	const [cashBenefits, setCashBenefits] = useState<Transaction[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [isExpanded, setIsExpanded] = useState(false);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [nonCashBenefits, setNonCashBenefits] = useState<Transaction[]>([]);
	const [tabValue, setTabValue] = useState('cashBenefits');
	const [users, setUsers] = useState<User[]>([]);

	// fetch loans
	useFetch({
		isEnabled: Boolean(beneficiary),
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${beneficiary.id}&fields=id,idWbg,entities&populate=entities`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch users
	const { isLoading: isLoadingUsers } = useFetch({
		isEnabled: Boolean(beneficiary && isExpanded && tabValue === 'users'),
		url: `${process.env.REACT_APP_API_URL}/users?entities=${beneficiary.id}&populate=country`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}
		},
	});

	// fetch cash benefits
	const { isLoading: isLoadingCashBenefits } = useFetch({
		isEnabled: Boolean(beneficiary),
		url: `${process.env.REACT_APP_API_URL}/transactions?type=CASH_BENEFIT&toEntity=${beneficiary.id}&populate=expenseType,fromAccount,payments.conversions,payments.loan`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashBenefits(res.data);
			}
		},
	});

	// fetch non cash benefits
	const { isLoading: isLoadingNonCashBenefits } = useFetch({
		isEnabled: Boolean(beneficiary && isExpanded && tabValue === 'nonCashBenefits'),
		url: `${process.env.REACT_APP_API_URL}/transactions?type=NON_CASH_BENEFIT&toEntity=${beneficiary.id}&populate=benefit,fromEntity`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setNonCashBenefits(res.data);
			}
		},
	});

	// fetch documents
	const urlFetchDocs = `${process.env.REACT_APP_API_URL}/documents?entities=${beneficiary.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`;

	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(beneficiary && isExpanded && tabValue === 'documents'),
		url: urlFetchDocs,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogBeneficiary');
		setSearchParams(searchParams);
	};

	// vars
	const associatedEntity = loans?.[0]?.entities?.[0]?.parent;

	// metadata vars
	let metadataProtected;
	let metadataTotalBenefits;
	let metadataCurrency;

	const tabs = [];

	if (userProjectPermissions?.cashBenefit?.read) {
		tabs.push({
			label: t('cashBenefits'),
			value: 'cashBenefits',
		});
	}

	if (userProjectPermissions?.nonCashBenefit?.read) {
		tabs.push({
			label: t('nonCashBenefits'),
			value: 'nonCashBenefits',
		});
	}

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (userProjectPermissions?.user?.read) {
		tabs.push({
			label: t('users'),
			value: 'users',
		});
	}

	if (beneficiary.metadata && Array.isArray(beneficiary?.metadata)) {
		// set publicly accessible metadata
		metadataTotalBenefits = beneficiary?.metadata?.find(
			(metadata) => metadata.name === 'totalBenefits'
		);
		metadataCurrency = beneficiary?.metadata?.find((metadata) => metadata.name === 'currency');

		// filter protected metadata
		metadataProtected = beneficiary.metadata.filter((metadata) => metadata.isProtected);

		if (metadataProtected.length) {
			tabs.push({
				label: t('metadata'),
				value: 'metadata',
			});
		}
	}
	let totalBenefitsValue;
	if (metadataTotalBenefits?.value && metadataCurrency?.value) {
		totalBenefitsValue = formatCurrency({
			currency: String(metadataCurrency.value),
			value: Number(metadataTotalBenefits.value),
		});
	} else if (metadataTotalBenefits?.value) {
		totalBenefitsValue = metadataTotalBenefits.value;
	} else {
		totalBenefitsValue = t('na');
	}

	const payments: Payment[] = [];

	// for each cash benefit
	for (let i = 0; i < cashBenefits.length; i++) {
		const transaction = cashBenefits[i];

		// for each payment
		for (let p = 0; p < transaction.payments.length; p++) {
			transaction.payments[p].transaction = transaction as Transaction;
			payments.push(transaction.payments[p]);
		}
	}

	const btnMenuItems: MenuItemProps[] = [];

	if (userProjectPermissions?.cashBenefit?.create) {
		btnMenuItems.push({
			children: t('recordCashBenefit'),
			icon: <IconPaid />,
			onClick: () => {
				searchParams.set('dialog', 'recordCashBenefit');
				searchParams.set('dialogBeneficiary', String(beneficiary.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.nonCashBenefit?.create) {
		btnMenuItems.push({
			children: t('recordNonCashBenefit'),
			icon: <IconVolunteerActivism />,
			onClick: () => {
				searchParams.set('dialog', 'recordNonCashBenefit');
				searchParams.set('dialogBeneficiary', String(beneficiary.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsBeneficiary');
				searchParams.set('dialogBeneficiary', String(beneficiary.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.beneficiary?.update) {
		btnMenuItems.push({
			children: t('editBeneficiary'),
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editBeneficiary');
				searchParams.set('dialogBeneficiary', String(beneficiary.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.beneficiary?.update) {
		if (beneficiary.wallet?.status !== 'INACTIVE') {
			btnMenuItems.push({
				children: t('deactivateBeneficiary'),
				icon: <IconDoDisturb color="error" />,
				onClick: () => {
					searchParams.set('dialog', 'updateBeneficiaryStatus');
					searchParams.set('dialogBeneficiary', String(beneficiary.id));
					setSearchParams(searchParams);
				},
			});
		} else {
			btnMenuItems.push({
				children: t('activateBeneficiary'),
				icon: <IconPowerSettingsNew color="success" />,
				onClick: () => {
					searchParams.set('dialog', 'updateBeneficiaryStatus');
					searchParams.set('dialogBeneficiary', String(beneficiary.id));
					setSearchParams(searchParams);
				},
			});
		}
	}

	const currency = payments?.[0]?.currency;
	const totalPaid = payments.reduce((total, payment) => {
		return (total += payment.amount);
	}, 0);

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
						overflow: 'hidden',
					};
				}}>
				<Box sx={{ padding: theme.spacing(1.5) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={2}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle type={beneficiary.subtype?.toLowerCase() as IconCircleType} />
									</Grid>
									<Grid item>
										<TypographyCaption id={beneficiary.idExternal} />
										<LinkEntity entity={beneficiary} />
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('wallet')}
								value={
									beneficiary.wallet?.address ? (
										<BlockchainHash hash={beneficiary.wallet.address} />
									) : (
										t('na')
									)
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('type')}
								value={<Typography>{translateType(beneficiary.subtype, t)}</Typography>}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('entity')}
								value={<LinkEntity entity={associatedEntity} hasEllipsis={true} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('status')}
								value={<ChipStatus status={beneficiary.wallet?.status || 'ACTIVE'} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('benefits')}
								value={
									<Grid container direction="column">
										<Grid item>
											<Typography fontWeight={500} variant="caption">
												{t('paid')}{' '}
												<Amount
													currency={currency}
													notation="compact"
													TypographyProps={{ fontWeight: 500, variant: 'caption' }}
													value={totalPaid}
												/>
											</Typography>
										</Grid>
										<Grid item>
											<Typography color="text.secondary" variant="caption">
												{t('total')} {totalBenefitsValue}
											</Typography>
										</Grid>
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('transactionsEvidenced')}
								value={
									<ChipTraceability traceability={beneficiary.traceability} type="TRANSACTIONS" />
								}
							/>
						</Grid>
						{featureFlags['FINANCIAL_TRACEABILITY'] && (
							<Grid item xs={12} lg={1}>
								<RowKeyValue
									label={t('financialValueEvidenced')}
									value={
										<ChipTraceability traceability={beneficiary.traceability} type="FINANCIAL" />
									}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										entity={beneficiary}
										activityTypes={['ENTITY_CREATED', 'ENTITY_UPDATED']}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={true}>
							<Grid container justifyContent={{ xs: 'space-between', lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								{tabs.length > 0 && (
									<Grid item>
										<BtnExpand
											isExpanded={isExpanded}
											label={isExpanded ? t('hideDetails') : t('seeDetails')}
											onClick={handleToggleCard}
										/>
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && tabs.length > 0 && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="cashBenefits">
							<TableBasicPayments
								data={payments}
								headersIncluded={[
									'amount',
									'fromAccount',
									'hash',
									'recorded',
									'reimbursed',
									'source',
									'status',
									'tax',
									'total',
								]}
								isLoading={isLoadingCashBenefits}
							/>
						</TabPanel>
						<TabPanel value="nonCashBenefits">
							<TableBasicNonCashBenefits
								data={nonCashBenefits}
								isLoading={isLoadingNonCashBenefits}
							/>
						</TabPanel>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
						<TabPanel value="users">
							<TableBasicUsers data={users} isLoading={isLoadingUsers} />
						</TabPanel>
						<TabPanel value="metadata">
							<Box
								sx={(theme) => {
									return {
										backgroundColor: theme.palette.brand.blue[100],
									};
								}}>
								<Metadata data={beneficiary?.metadata as MetadataField[]} />
							</Box>
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogAddTransaction
				DialogActionsProps={{ subtitle: { children: beneficiary.name } }}
				isOpen={
					paramDialog === 'recordCashBenefit' && paramDialogBeneficiary === String(beneficiary.id)
				}
				onClose={handleCloseDialog}
				type="CASH_BENEFIT"
			/>
			<DialogAddNonCashBenefit
				DialogActionsProps={{ subtitle: { children: beneficiary.name } }}
				isOpen={
					paramDialog === 'recordNonCashBenefit' &&
					paramDialogBeneficiary === String(beneficiary.id)
				}
				onClose={handleCloseDialog}
			/>
			<DialogEditBeneficiary
				DialogActionsProps={{ subtitle: { children: beneficiary.name } }}
				isOpen={
					paramDialog === 'editBeneficiary' && paramDialogBeneficiary === String(beneficiary.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchBeneficiaries) {
						refetchBeneficiaries();
					}
				}}
			/>
			<DialogUpdateEntityStatus
				entity={beneficiary}
				isOpen={
					paramDialog === 'updateBeneficiaryStatus' &&
					paramDialogBeneficiary === String(beneficiary.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchBeneficiaries) {
						refetchBeneficiaries();
					}
				}}
			/>
			<DialogUploadDocsEntity
				entity={beneficiary}
				isOpen={
					paramDialog === 'uploadDocsBeneficiary' &&
					paramDialogBeneficiary === String(beneficiary.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchBeneficiaries) {
						refetchBeneficiaries();
					}
				}}
			/>
		</>
	);
};
